import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, CheckCircle2, Loader2, Send } from "lucide-react";
import Reveal from "@/components/Reveal";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Partner() {
  const t = useT();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    plan_interest: "",
    monthly_volume: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/partners/contact`, form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", company: "", phone: "", plan_interest: "", monthly_volume: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail) ? err.response.data.detail.map((d) => d.msg).join(", ") : "") ||
        t.partner.errorFallback;
      setStatus({ state: "error", error: typeof detail === "string" ? detail : t.partner.errorFallback });
    }
  };

  return (
    <>
      <section className="relative bg-delfi-dark text-white pt-36 pb-24 overflow-hidden" data-testid="partner-hero">
        <div className="chevron-bg right-[-8%] top-[-18%] opacity-[0.08]" aria-hidden="true">›</div>
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="delfi-container relative max-w-3xl">
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
            <Chevron size={14} /> {t.partner.eyebrow}
          </div>
          <h1 className="headline-display text-white mt-4 text-[clamp(2.2rem,5vw,4rem)]" data-testid="partner-title">
            {t.partner.titleLead} <span className="text-delfi-red">{t.partner.titleHighlight}</span> {t.partner.titleTrail}
          </h1>
          <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">{t.partner.description}</p>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="partner-form-section">
        <div className="delfi-container grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-delfi-red flex items-center gap-2">
                  <Chevron size={12} /> {t.partner.contactEyebrow}
                </p>
                <h2 className="headline-section text-3xl md:text-4xl text-delfi-navy mt-4">{t.partner.contactTitle}</h2>
                <p className="mt-4 text-delfi-navy/70 leading-relaxed">{t.partner.contactBody}</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center shrink-0">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <p className="font-semibold text-delfi-navy">{t.partner.headquarters}</p>
                    <p className="text-delfi-navy/70 text-sm mt-0.5">{t.partner.hqLocation}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="font-semibold text-delfi-navy">{t.partner.partnershipsLabel}</p>
                    <a href="mailto:hello@twasalawasal.com" className="text-delfi-navy/70 text-sm mt-0.5 hover:text-delfi-red">
                      hello@twasalawasal.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="font-semibold text-delfi-navy">{t.partner.businessLabel}</p>
                    <a href="mailto:business@twasalawasal.com" className="text-delfi-navy/70 text-sm mt-0.5 hover:text-delfi-red">
                      business@twasalawasal.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-delfi-cloud p-6 border-l-4 border-delfi-red">
                <p className="text-xs font-bold uppercase tracking-wider text-delfi-red">{t.partner.pilotEyebrow}</p>
                <p className="mt-2 text-delfi-navy/80 text-sm leading-relaxed">{t.partner.pilotBody}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-3">
            <div className="relative rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(46,46,46,0.25)] border border-delfi-navy/5 p-8 md:p-10">
              {status.state === "success" ? (
                <div className="py-12 text-center" data-testid="partner-form-success">
                  <div className="inline-flex w-16 h-16 rounded-full bg-delfi-red/10 text-delfi-red items-center justify-center">
                    <CheckCircle2 size={32} />
                  </div>
                  <h3 className="mt-6 text-2xl font-bold text-delfi-navy">{t.partner.success.title}</h3>
                  <p className="mt-3 text-delfi-navy/70 max-w-md mx-auto">{t.partner.success.body}</p>
                  <button
                    type="button"
                    onClick={() => setStatus({ state: "idle", error: "" })}
                    className="mt-8 btn-delfi-secondary"
                    data-testid="partner-form-send-another"
                  >
                    {t.common.sendAnother}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="partner-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.name}</label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} required minLength={2}
                        placeholder={t.partner.form.namePh} className="delfi-input" data-testid="partner-form-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.email}</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required
                        placeholder={t.partner.form.emailPh} className="delfi-input" data-testid="partner-form-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.company}</label>
                      <input type="text" name="company" value={form.company} onChange={handleChange} required
                        placeholder={t.partner.form.companyPh} className="delfi-input" data-testid="partner-form-company" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.phone}</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                        placeholder={t.partner.form.phonePh} className="delfi-input" data-testid="partner-form-phone" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.plan}</label>
                      <select name="plan_interest" value={form.plan_interest} onChange={handleChange}
                        className="delfi-input" data-testid="partner-form-plan">
                        <option value="">{t.partner.form.planPh}</option>
                        {(t.partner.planOptions || []).map((p, i) => (<option key={i} value={p}>{p}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.volume}</label>
                      <select name="monthly_volume" value={form.monthly_volume} onChange={handleChange}
                        className="delfi-input" data-testid="partner-form-volume">
                        <option value="">{t.partner.form.volumePh}</option>
                        {(t.partner.volumeOptions || []).map((v, i) => (<option key={i} value={v}>{v}</option>))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-delfi-navy mb-2">{t.partner.form.message}</label>
                    <textarea name="message" value={form.message} onChange={handleChange} required minLength={5} rows={5}
                      placeholder={t.partner.form.messagePh} className="delfi-input resize-none" data-testid="partner-form-message" />
                  </div>

                  {status.state === "error" && (
                    <div className="rounded-lg bg-delfi-red/10 border border-delfi-red/20 text-delfi-red text-sm p-4" data-testid="partner-form-error">
                      {status.error}
                    </div>
                  )}

                  <button type="submit" disabled={status.state === "loading"}
                    className="btn-delfi-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    data-testid="partner-form-submit">
                    {status.state === "loading" ? (
                      <><Loader2 size={18} className="animate-spin" /> {t.common.sending}</>
                    ) : (
                      <><Send size={18} /> {t.common.sendEnquiry}</>
                    )}
                  </button>
                  <p className="text-xs text-delfi-navy/55 text-center">{t.partner.form.consent}</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
